import { useParams, useLocation, Link } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft, Package, Plus, X, Upload, GripVertical, Trash2, Loader2, Link2, ImagePlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { AdImage } from "@/components/ad-image";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { useState, useRef, useEffect } from "react";
import type { Product } from "@shared/schema";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MIB } from "@shared/upload-limits";

function fileTooLargeMessage(file: File): string | null {
  if (file.size <= IMAGE_UPLOAD_MAX_BYTES) return null;
  return `${file.name}: file is too large. Choose an image up to ${IMAGE_UPLOAD_MAX_MIB} MiB.`;
}

async function uploadErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.message === "string" && body.message.trim()) return body.message.trim();
  } catch {
    // The endpoint may return plain text or an empty proxy response.
  }
  return `Upload failed (${response.status}${response.statusText ? ` ${response.statusText}` : ""})`;
}

interface VariantOption {
  title: string;
  values: string[];
}

interface Variant {
  name: string;
  price: string;
  imageUrl?: string;
  description?: string;
}

const formSchema = z.object({
  name: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  category: z.string().optional(),
  productRole: z.string().optional(),
  summary: z.string().optional(),
  jobToBeDone: z.string().optional(),
  priceText: z.string().optional(),
  sku: z.string().optional(),
  pdpUrl: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

function MediaSection({
  media,
  onAdd,
  onRemove,
  onError,
  onUploadingChange,
}: {
  media: string[];
  onAdd: (urls: string[]) => void;
  onRemove: (index: number) => void;
  onError: (msg: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(24);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    const urls: string[] = [];
    const errors: string[] = [];
    for (const file of Array.from(files)) {
      const sizeError = fileTooLargeMessage(file);
      if (sizeError) {
        errors.push(sizeError);
        continue;
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await fetch("/api/upload", {
          method: "POST",
          body: formData,
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          urls.push(data.url);
        } else {
          errors.push(`${file.name}: ${await uploadErrorMessage(res)}`);
        }
      } catch (error) {
        errors.push(`${file.name}: ${error instanceof Error ? error.message : "Upload failed"}`);
      }
    }
    if (urls.length > 0) onAdd(urls);
    if (errors.length > 0) onError(errors.join("\n"));
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="rounded-xl border bg-card p-5">
      <h3 className="text-sm font-semibold mb-3" data-testid="text-media-label">Media</h3>
      <div className="flex flex-wrap gap-3">
        {media.slice(0, visibleCount).map((url, i) => (
          <div key={i} className="relative group w-28 h-28 rounded-lg border overflow-hidden bg-muted">
            <AdImage src={url} thumbWidth={224} alt="Product photo" className="w-full h-full object-cover" placeholderClassName="!min-w-0 !min-h-0 p-1 text-center" data-testid={`image-product-media-${i}`} />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`button-remove-media-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {media.length > visibleCount && (
          <button
            type="button"
            onClick={() => setVisibleCount((count) => count + 24)}
            className="w-28 h-28 rounded-lg border border-dashed flex flex-col items-center justify-center text-xs text-muted-foreground hover:text-foreground"
            data-testid="button-more-product-media"
          >
            <span>Show 24 more</span>
            <span>{media.length - visibleCount} remaining</span>
          </button>
        )}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-28 h-28 rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors cursor-pointer"
          data-testid="button-add-media"
        >
          {uploading ? (
            <span className="text-[10px]">Uploading...</span>
          ) : (
            <>
              <Plus className="h-5 w-5" />
              <span className="text-[10px]">Add</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleUpload}
      />
    </div>
  );
}

function VariantOptionsEditor({
  options,
  onChange,
}: {
  options: VariantOption[];
  onChange: (options: VariantOption[]) => void;
}) {
  const [newValue, setNewValue] = useState<Record<number, string>>({});

  const addOption = () => {
    onChange([...options, { title: "", values: [] }]);
  };

  const removeOption = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const updateTitle = (index: number, title: string) => {
    const updated = [...options];
    updated[index] = { ...updated[index], title };
    onChange(updated);
  };

  const addValue = (optionIndex: number) => {
    const val = (newValue[optionIndex] || "").trim();
    if (!val) return;
    const updated = [...options];
    if (!updated[optionIndex].values.includes(val)) {
      updated[optionIndex] = {
        ...updated[optionIndex],
        values: [...updated[optionIndex].values, val],
      };
      onChange(updated);
    }
    setNewValue({ ...newValue, [optionIndex]: "" });
  };

  const removeValue = (optionIndex: number, valueIndex: number) => {
    const updated = [...options];
    updated[optionIndex] = {
      ...updated[optionIndex],
      values: updated[optionIndex].values.filter((_, i) => i !== valueIndex),
    };
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {options.map((option, oi) => (
        <div key={oi} className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1">
              <Input
                value={option.title}
                onChange={(e) => updateTitle(oi, e.target.value)}
                placeholder="Option name (e.g., Choose your flavor)"
                className="font-medium"
                data-testid={`input-option-title-${oi}`}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => removeOption(oi)}
              data-testid={`button-remove-option-${oi}`}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 pl-6">
            {option.values.map((val, vi) => (
              <Badge key={vi} variant="secondary" className="text-xs gap-1 py-1 px-2.5">
                {val}
                <button
                  type="button"
                  onClick={() => removeValue(oi, vi)}
                  data-testid={`button-remove-value-${oi}-${vi}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 pl-6">
            <Input
              value={newValue[oi] || ""}
              onChange={(e) => setNewValue({ ...newValue, [oi]: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addValue(oi);
                }
              }}
              placeholder="Add value..."
              className="text-sm"
              data-testid={`input-option-value-${oi}`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addValue(oi)}
              data-testid={`button-add-value-${oi}`}
            >
              Add
            </Button>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addOption}
        className="rounded-full"
        data-testid="button-add-option"
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add another option
      </Button>
    </div>
  );
}

function VariantsTable({
  variants,
  onChange,
  onError,
  media = [],
}: {
  variants: Variant[];
  onChange: (variants: Variant[]) => void;
  onError: (message: string) => void;
  /** Task #2073 — the product's existing photos, offered as variant-image picks. */
  media?: string[];
}) {
  // Task #744 — per-variant picture upload. Tracks which row is mid-upload so we
  // can show a spinner without blocking the others.
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Task #2073 — which row's image picker popover is open (upload OR pick from
  // the product's existing photos, so size-only/unmatched Shopify variants can
  // still get an exact reference image).
  const [imagePickerIndex, setImagePickerIndex] = useState<number | null>(null);
  const [pickerVisibleCount, setPickerVisibleCount] = useState(24);

  const addVariant = () => {
    onChange([...variants, { name: "", price: "" }]);
  };

  const updateVariant = (index: number, field: keyof Variant, value: string | number) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeVariant = (index: number) => {
    onChange(variants.filter((_, i) => i !== index));
  };

  const handleVariantImageUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const sizeError = fileTooLargeMessage(file);
    if (sizeError) {
      onError(sizeError);
      e.target.value = "";
      return;
    }
    setUploadingIndex(index);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: formData, credentials: "include" });
      if (!res.ok) throw new Error(await uploadErrorMessage(res));
      const data = await res.json();
      updateVariant(index, "imageUrl", data.url);
    } catch (error) {
      onError(`${file.name}: ${error instanceof Error ? error.message : "Upload failed"}`);
    } finally {
      setUploadingIndex(null);
      const ref = fileInputRefs.current[index];
      if (ref) ref.value = "";
    }
  };

  return (
    <div>
      {variants.length > 0 && (
        <div className="space-y-3">
          {variants.map((variant, i) => (
            <div
              key={i}
              className="border rounded-lg p-4 space-y-3"
              data-testid={`row-variant-${i}`}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    ref={(el) => { fileInputRefs.current[i] = el; }}
                    onChange={(e) => handleVariantImageUpload(i, e)}
                    data-testid={`input-variant-image-file-${i}`}
                  />
                  <Popover
                    open={imagePickerIndex === i}
                    onOpenChange={(open) => {
                      // Task #2073 — without product photos there is nothing to
                      // pick from, so the tile keeps its original direct-upload
                      // behavior instead of opening a one-option popover.
                      if (open && media.length === 0) {
                        fileInputRefs.current[i]?.click();
                        return;
                      }
                      if (open) setPickerVisibleCount(24);
                      setImagePickerIndex(open ? i : null);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="relative h-16 w-16 rounded-lg border border-dashed border-border bg-muted/40 flex items-center justify-center overflow-hidden hover:bg-muted/70 transition-colors"
                        data-testid={`button-variant-image-${i}`}
                      >
                        {uploadingIndex === i ? (
                          <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
                        ) : variant.imageUrl ? (
                          <AdImage src={variant.imageUrl} thumbWidth={160} alt={variant.name || "Variant"} className="h-full w-full object-cover" placeholderClassName="!min-w-0 !min-h-0 text-center [&_svg]:h-4 [&_svg]:w-4" />
                        ) : (
                          <ImagePlus className="h-5 w-5 text-muted-foreground" />
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="start">
                      <p className="text-xs font-medium mb-2">Variant image</p>
                      <div className="grid grid-cols-4 gap-1.5 mb-2 max-h-40 overflow-y-auto">
                        {media.slice(0, pickerVisibleCount).map((url, j) => (
                          <button
                            key={j}
                            type="button"
                            onClick={() => {
                              updateVariant(i, "imageUrl", url);
                              setImagePickerIndex(null);
                            }}
                            className={`relative aspect-square rounded-md border overflow-hidden hover:ring-2 hover:ring-primary transition-shadow ${variant.imageUrl === url ? "ring-2 ring-primary" : ""}`}
                            data-testid={`button-variant-image-pick-${i}-${j}`}
                          >
                            <AdImage src={url} thumbWidth={128} alt="Product photo" className="h-full w-full object-cover" placeholderClassName="!min-w-0 !min-h-0 text-center [&_svg]:h-4 [&_svg]:w-4" />
                          </button>
                        ))}
                      </div>
                      {media.length > pickerVisibleCount && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="w-full h-8 text-xs mb-2"
                          onClick={() => setPickerVisibleCount((count) => count + 24)}
                          data-testid={`button-more-variant-images-${i}`}
                        >
                          Show 24 more ({media.length - pickerVisibleCount} remaining)
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full h-8 text-xs"
                        onClick={() => {
                          setImagePickerIndex(null);
                          fileInputRefs.current[i]?.click();
                        }}
                        data-testid={`button-variant-image-upload-${i}`}
                      >
                        <Upload className="h-3.5 w-3.5 mr-1.5" />
                        Upload new image
                      </Button>
                    </PopoverContent>
                  </Popover>
                  {variant.imageUrl && (
                    <button
                      type="button"
                      onClick={() => updateVariant(i, "imageUrl", "")}
                      className="mt-1 w-full text-[10px] text-muted-foreground hover:text-destructive transition-colors"
                      data-testid={`button-variant-image-remove-${i}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="flex-1 grid grid-cols-[1fr_140px] gap-2 items-center">
                  <Input
                    value={variant.name}
                    onChange={(e) => updateVariant(i, "name", e.target.value)}
                    placeholder="Variant name"
                    className="h-9 text-sm font-medium"
                    data-testid={`input-variant-name-${i}`}
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">$</span>
                    <Input
                      value={variant.price}
                      onChange={(e) => updateVariant(i, "price", e.target.value)}
                      placeholder="0.00"
                      className="h-9 text-sm"
                      data-testid={`input-variant-price-${i}`}
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => removeVariant(i)}
                  data-testid={`button-remove-variant-${i}`}
                >
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </div>
              {/* Task #744 — variants only need a Description override
                  (ingredients live in the description); the product-level
                  ingredients/features list still feeds generation. */}
              <div>
                <Label className="text-[11px] text-muted-foreground">Description</Label>
                <Textarea
                  value={variant.description || ""}
                  onChange={(e) => updateVariant(i, "description", e.target.value)}
                  placeholder="Overrides the product description for this variant (leave blank to inherit)"
                  className="mt-1 text-sm min-h-[64px]"
                  data-testid={`input-variant-description-${i}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addVariant}
        className="mt-3 rounded-full"
        data-testid="button-add-variant"
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add variant
      </Button>
    </div>
  );
}

const BLANK_FORM_VALUES: FormValues = {
  name: "",
  description: "",
  category: "",
  productRole: "",
  summary: "",
  jobToBeDone: "",
  priceText: "",
  sku: "",
  pdpUrl: "",
};

interface ProductCreatePageProps {
  /**
   * Explicit mode for this page. We do NOT derive create-vs-edit from
   * `useParams().productId` because wouter v3's `useCachedParams` (in its
   * <Route> component) only checks keys present in the NEW match — it never
   * removes keys that disappeared. Inside a <Switch>, the matched <Route>
   * element gets the same React fiber reused across matches (same type, no
   * key), so its cached params useRef is shared between e.g.
   * `/brands/:brandId/products/:productId` and `/brands/:brandId/products/new`.
   * After visiting a product detail page, going back to `/products/new` would
   * therefore make `useParams().productId` still return the prior product's id,
   * tricking this page into edit-mode and hydrating from the cached product.
   * Passing mode/productId as explicit props from App.tsx bypasses that quirk.
   */
  mode: "create" | "edit";
  productIdProp?: string;
}

export default function ProductCreatePage({ mode, productIdProp }: ProductCreatePageProps) {
  const { brandId } = useParams<{ brandId: string }>();
  const productId = mode === "edit" ? productIdProp : undefined;
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [media, setMedia] = useState<string[]>([]);
  const [variantOptions, setVariantOptions] = useState<VariantOption[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [isBundle, setIsBundle] = useState(false);
  const [bundleProductIds, setBundleProductIds] = useState<string[]>([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  // Bumped on each form reset so child editors with transient state remount fresh.
  const [resetCounter, setResetCounter] = useState(0);
  const isEdit = mode === "edit";
  const [initialized, setInitialized] = useState(!isEdit);

  const { data: existingProduct, isLoading: productLoading } = useQuery<Product>({
    // Use a distinct, never-shared key when not in edit mode so a stale
    // entry for an actual product id can never hydrate the create form.
    queryKey: isEdit ? ["/api/products", productId] : ["/api/products", "__create__"],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: isEdit,
  });

  const { data: allProducts } = useQuery<Product[]>({
    queryKey: ["/api/brands", brandId, "products"],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!brandId && isBundle,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: BLANK_FORM_VALUES,
  });

  // In create mode, force a blank slate on every mount so nothing from a
  // prior product can leak in (form state, media, variants, bundle, etc.).
  useEffect(() => {
    if (!isEdit) {
      form.reset(BLANK_FORM_VALUES);
      setMedia([]);
      setVariantOptions([]);
      setVariants([]);
      setIsBundle(false);
      setBundleProductIds([]);
      setInitialized(true);
    }
    // Mount-only: routes use a route-scoped `key` to remount on productId change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEdit && existingProduct && !initialized) {
      form.reset({
        name: existingProduct.name || "",
        description: existingProduct.description || "",
        category: existingProduct.category || "",
        productRole: existingProduct.productRole || "",
        summary: existingProduct.summary || "",
        jobToBeDone: existingProduct.jobToBeDone || "",
        priceText: existingProduct.priceText || "",
        sku: existingProduct.sku || "",
        pdpUrl: existingProduct.pdpUrl || "",
      });
      const parsedMedia = existingProduct.mediaJson as string[] | null;
      if (Array.isArray(parsedMedia)) setMedia(parsedMedia);
      const parsedOptions = existingProduct.variantOptionsJson as VariantOption[] | null;
      if (Array.isArray(parsedOptions)) setVariantOptions(parsedOptions);
      const parsedVariants = existingProduct.variantsJson as Variant[] | null;
      if (Array.isArray(parsedVariants)) setVariants(parsedVariants);
      if (existingProduct.isBundle) setIsBundle(true);
      const parsedBundleIds = existingProduct.bundleProductIds as string[] | null;
      if (Array.isArray(parsedBundleIds)) setBundleProductIds(parsedBundleIds);
      setInitialized(true);
    }
  }, [isEdit, existingProduct, initialized, form]);

  type AfterSaveAction = "close" | "addNew";
  const [pendingAction, setPendingAction] = useState<AfterSaveAction | null>(null);

  const saveMutation = useMutation({
    mutationFn: async ({ values }: { values: FormValues; afterSave: AfterSaveAction }) => {
      const slug = values.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
        .slice(0, 80);
      const body: any = {
        ...values,
        slug,
        mediaJson: media.length > 0 ? media : null,
        variantOptionsJson: variantOptions.length > 0 ? variantOptions : null,
        variantsJson: variants.length > 0 ? variants : null,
        isBundle,
        bundleProductIds: isBundle && bundleProductIds.length > 0 ? bundleProductIds : null,
      };
      Object.keys(body).forEach((k) => {
        if (body[k] === "") delete body[k];
      });
      if (isEdit) {
        const res = await apiRequest("PATCH", `/api/products/${productId}`, body);
        return res.json();
      } else {
        const res = await apiRequest("POST", `/api/brands/${brandId}/products`, body);
        return res.json();
      }
    },
    onSuccess: (product: Product, variables) => {
      // Refresh every consumer of the brand-products list. All other pages
      // use the template-literal string key form, so invalidate that one.
      // We also invalidate the array-key form for safety in case any consumer
      // adopts it later, plus a prefix invalidation as a final safety net.
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/products`] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "products"] });
      queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          if (!Array.isArray(k) || k.length === 0) return false;
          const first = k[0];
          return typeof first === "string" && first.includes(`/api/brands/${brandId}/products`);
        },
      });
      // Pre-seed detail cache so the redirect lands on fresh data instantly.
      if (product?.id) {
        queryClient.setQueryData(["/api/products", product.id], product);
      }
      if (isEdit && productId) {
        queryClient.invalidateQueries({ queryKey: ["/api/products", productId] });
      }
      toast({ title: isEdit ? "Product updated" : "Product created" });
      setPendingAction(null);
      if (variables.afterSave === "addNew") {
        // Reset to a blank form so the user can immediately add another.
        form.reset(BLANK_FORM_VALUES);
        setMedia([]);
        setVariantOptions([]);
        setVariants([]);
        setIsBundle(false);
        setBundleProductIds([]);
        // Bump the reset counter so child editors with transient local state
        // (e.g. VariantOptionsEditor's pending input values) remount fresh.
        setResetCounter((n) => n + 1);
        if (isEdit) {
          // From edit mode, navigate to the create route for a clean slate.
          navigate(`/brands/${brandId}/products/new`);
        } else {
          // Already on create route — stay here with the freshly reset form.
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } else {
        // "Save and Close" — return to the products dashboard.
        navigate(`/brands/${brandId}/products`);
      }
    },
    onError: (e: Error) => {
      setPendingAction(null);
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const handleSubmit = (afterSave: AfterSaveAction) => {
    if (mediaUploading) {
      toast({
        title: "Please wait",
        description: "Media is still uploading. Try again in a moment.",
      });
      return;
    }
    const values = form.getValues();
    const result = formSchema.safeParse(values);
    if (!result.success) {
      form.trigger();
      return;
    }
    setPendingAction(afterSave);
    saveMutation.mutate({ values, afterSave });
  };

  if (isEdit && productLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[200px] w-full rounded-xl" />
        <Skeleton className="h-[150px] w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href={isEdit ? `/brands/${brandId}/products/${productId}` : `/brands/${brandId}/products`}>
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="button-back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                {isEdit ? "Edit Product" : "New Product"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isEdit ? "Update product details" : "Add a new product to your catalog"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit("addNew")}
            disabled={saveMutation.isPending || mediaUploading}
            className="rounded-full"
            data-testid="button-save-and-add-new"
          >
            {saveMutation.isPending && pendingAction === "addNew" && (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            )}
            {saveMutation.isPending && pendingAction === "addNew" ? "Saving..." : "Save and Add New"}
          </Button>
          <Button
            onClick={() => handleSubmit("close")}
            disabled={saveMutation.isPending || mediaUploading}
            className="rounded-full"
            data-testid="button-save-and-close"
          >
            {saveMutation.isPending && pendingAction === "close" && (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            )}
            {saveMutation.isPending && pendingAction === "close" ? "Saving..." : "Save and Close"}
          </Button>
        </div>
      </div>

      <Form {...form}>
        <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
          <div className="rounded-xl border bg-card p-5 space-y-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Title</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., The Lip Duo" {...field} data-testid="input-product-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe your product in detail..."
                      className="min-h-[120px] resize-y"
                      {...field}
                      data-testid="input-product-description"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <MediaSection
            media={media}
            onUploadingChange={setMediaUploading}
            onAdd={(urls) => setMedia((prev) => [...prev, ...urls])}
            onRemove={(index) => setMedia((prev) => prev.filter((_, i) => i !== index))}
            onError={(msg) => toast({ title: "Upload Error", description: msg, variant: "destructive" })}
          />

          <div className="rounded-xl border bg-card p-5 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Category</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Lip Balms & Treatments" {...field} data-testid="input-category" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="productRole"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Product Role</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-product-role">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="hero">Hero</SelectItem>
                        <SelectItem value="bridge">Bridge</SelectItem>
                        <SelectItem value="aov">AOV Booster</SelectItem>
                        <SelectItem value="retention">Retention</SelectItem>
                        <SelectItem value="seasonal">Seasonal</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="priceText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Price</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., $37.95" {...field} data-testid="input-price" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">SKU</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., LIP-DUO-001" {...field} data-testid="input-sku" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="rounded-xl border bg-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link2 className="h-4 w-4 text-primary" />
                <div>
                  <Label className="text-sm font-medium" data-testid="label-bundle">Bundle</Label>
                  <p className="text-xs text-muted-foreground">Mark this product as a bundle and link included products</p>
                </div>
              </div>
              <Switch
                checked={isBundle}
                onCheckedChange={(checked) => {
                  setIsBundle(checked);
                  if (!checked) setBundleProductIds([]);
                }}
                data-testid="switch-is-bundle"
              />
            </div>
            {isBundle && (
              <div className="pt-2 space-y-3">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Included Products</Label>
                {(() => {
                  const availableProducts = (allProducts || []).filter(
                    (p) => p.id !== productId && p.status !== "archived"
                  );
                  if (availableProducts.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground italic">No other products in this brand yet.</p>
                    );
                  }
                  return (
                    <div className="space-y-2 max-h-[240px] overflow-y-auto rounded-lg border p-3">
                      {availableProducts.map((p) => {
                        const isSelected = bundleProductIds.includes(p.id);
                        return (
                          <label
                            key={p.id}
                            className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                            data-testid={`label-bundle-product-${p.id}`}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setBundleProductIds([...bundleProductIds, p.id]);
                                } else {
                                  setBundleProductIds(bundleProductIds.filter((id) => id !== p.id));
                                }
                              }}
                              data-testid={`checkbox-bundle-product-${p.id}`}
                            />
                            <div className="flex items-center gap-2 min-w-0">
                              {(() => {
                                const pMedia = p.mediaJson as string[] | null;
                                if (Array.isArray(pMedia) && pMedia.length > 0) {
                                  return (
                                    <AdImage
                                      src={pMedia[0]}
                                      thumbWidth={64}
                                      alt={p.name}
                                      className="h-8 w-8 rounded-md object-cover shrink-0"
                                      placeholderClassName="!min-w-0 !min-h-0 text-center [&_svg]:h-4 [&_svg]:w-4"
                                    />
                                  );
                                }
                                return (
                                  <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                                    <Package className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                );
                              })()}
                              <div className="min-w-0">
                                <span className="text-sm font-medium truncate block">{p.name}</span>
                                {p.priceText && (
                                  <span className="text-xs text-muted-foreground">{p.priceText}</span>
                                )}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}
                {bundleProductIds.length > 0 && (
                  <p className="text-xs text-muted-foreground" data-testid="text-bundle-count">
                    {bundleProductIds.length} product{bundleProductIds.length !== 1 ? "s" : ""} in this bundle
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-card p-5 space-y-5">
            <FormField
              control={form.control}
              name="summary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Summary</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Brief product summary for quick reference..."
                      className="min-h-[80px] resize-y"
                      {...field}
                      data-testid="input-summary"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="jobToBeDone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Job to Be Done</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="What job does the customer hire this product to do?"
                      className="min-h-[80px] resize-y"
                      {...field}
                      data-testid="input-job-to-be-done"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pdpUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">PDP URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} data-testid="input-pdp-url" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold" data-testid="text-variants-label">Variants</h3>
            </div>
            <VariantOptionsEditor key={`vopts-${resetCounter}`} options={variantOptions} onChange={setVariantOptions} />
            <div className="mt-6">
              <VariantsTable variants={variants} onChange={setVariants} media={media} onError={(msg) => toast({ title: "Upload Error", description: msg, variant: "destructive" })} />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
